import React, { useCallback, useState } from "react";
import { useMount } from "react-use";
import { Content as Inner, LeftPanel, LeftSidebar, Main, PageLayout, TopNavigation } from "@atlaskit/page-layout";
import { Box, ContainerProps, Grid, Typography } from "@mui/material";

import { NotificationBannerSlot } from "ui/NotificationBanner/NotificationBannerSlot";

import { useUpdateDocTitle } from "fond/hooks/useUpdateDocTitle";
import TopBar from "fond/topBar/TopBar";
import { useResizeObserver } from "fond/utils/hooks";
import localStorage from "fond/utils/localStorage";
import { HomeIconBarContent } from "fond/views/Home/HomeIconBarContent";
import { Footer } from "fond/widgets";
import { SuspenseSpinner } from "fond/widgets/SuspenseSpinner";

import { IconBar } from "../IconBar";

import { DEFAULT_TOPBAR_HEIGHT } from "./constants";

import "./PageContent.scss";
import { Content, Subtitle } from "./PageContent.styles";

export interface IProps {
  /**
   * The window title text content. Used to set the window title, if not provided the title will be used.
   */
  windowTitle?: string;
  /**
   * If of type string, the title text will be rendered as H1.
   */
  title: React.ReactNode | string;
  /**
   * The title text content.
   */
  subTitle?: string;
  /**
   * A collection of primary call to actions rendered to the right of the page heading
   */
  action?: React.ReactNode;
  /**
   * The main component of the page, such as project BOM or list of projects
   */
  mainContent: React.ReactNode;
  /**
   * Optional breadcrumbs section to be displayed under the page heading
   */
  breadcrumbs?: React.ReactNode;
  /**
   * Option navigation to be rendered with the left sidebar.
   */
  navigation?: React.ReactNode;
  /**
   * The `testId` prop is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
   */
  testId?: string;
  /**
   * Optional icon bar to be rendered next to the main content.
   */
  iconBar?: React.ReactNode | false;
  /**
   * Maximum width of the content container. Defaults to "lg".
   * Use false for full width.
   */
  maxWidth?: ContainerProps["maxWidth"];
}

const STORAGE_KEY = "FOND_TOPNAV_SLOT_HEIGHT";

const PageContent: React.FC<IProps> = ({
  breadcrumbs = null,
  subTitle,
  title,
  action = null,
  mainContent,
  navigation = null,
  testId,
  iconBar = null,
  windowTitle,
  maxWidth = "lg",
}: IProps) => {
  let docTitle = "";
  if (windowTitle) {
    docTitle = windowTitle;
  } else if (typeof title === "string") {
    docTitle = title;
  }
  useUpdateDocTitle(docTitle);
  const topnavRef = React.useRef<HTMLDivElement>(null);
  const initialHeight = parseInt(localStorage.getItem(STORAGE_KEY, DEFAULT_TOPBAR_HEIGHT), 10);
  const [topNavHeight, setTopNavHeight] = useState<number>(initialHeight);

  let iconBarContent = iconBar;
  if (!iconBar && iconBar !== false) {
    iconBarContent = <HomeIconBarContent />;
  }

  const updateTopNavHeight = useCallback(() => {
    const height = topnavRef.current?.clientHeight ?? 0;
    setTopNavHeight(height);
    localStorage.setItem(STORAGE_KEY, height.toString());
  }, []);

  /**
   * Determines the required height for the toolbar based on any conditional content
   * such as the impersonating banner or other notification banners.
   */
  useResizeObserver(topnavRef, updateTopNavHeight);

  // make sure the initial height is set
  useMount(updateTopNavHeight);

  return (
    <PageLayout testId={testId}>
      <NotificationBannerSlot id="banner-slot" />
      <TopNavigation isFixed height={topNavHeight} id="top-navigation-slot">
        <div ref={topnavRef}>
          <TopBar />
        </div>
      </TopNavigation>
      {iconBarContent && (
        <LeftPanel width={77} isFixed id="left-panel-slot">
          <IconBar>{iconBarContent}</IconBar>
        </LeftPanel>
      )}
      <Inner testId="content">
        {navigation && <LeftSidebar testId="left-sidebar">{navigation}</LeftSidebar>}

        <Main>
          <Box display="flex" flexDirection="row" minHeight="100%">
            <Box flexGrow={1}>
              <Content maxWidth={maxWidth}>
                <Grid container item direction="column" flexGrow={1} position="relative" flexWrap="nowrap">
                  {(title || subTitle || breadcrumbs || action) && (
                    <Grid container item direction="row" justifyContent="space-between" style={{ paddingBottom: 24 }}>
                      <Grid item>
                        {typeof title === "string" ? (
                          <Typography variant="h1" data-testid="page-title">
                            {title}
                          </Typography>
                        ) : (
                          title
                        )}
                        {subTitle && <Subtitle variant="subtitle2">{subTitle}</Subtitle>}
                        {breadcrumbs}
                      </Grid>
                      {action && <Grid item>{action}</Grid>}
                    </Grid>
                  )}
                  <Grid item display="flex" flexDirection="column" flexGrow={1}>
                    <SuspenseSpinner name="main-content">{mainContent}</SuspenseSpinner>
                  </Grid>
                </Grid>
                <Grid item style={{ paddingTop: 42 }}>
                  <Footer />
                </Grid>
              </Content>
            </Box>
          </Box>
        </Main>
      </Inner>
    </PageLayout>
  );
};

export default PageContent;
